import * as React from "react";
import {useLocation} from "react-router-dom";

import {GameType as Pool} from "@atg-sport-shared/pool-types";

export {Pool};

export enum PoolNames {
    Big9 = "Big 9",
    Daily5 = "Daily 5",
}

export enum PoolSports {
    Football = "Football",
    Hockey = "Hockey",
}

enum Feature {
    Wildcard = "wildcard",
    Harry = "harry",
    GameTips = "gameTips",
    HowBig9Works = "howBig9Works",
    UpcomingOfferings = "upcomingOfferings",
    MultipleLastResults = "multipleLastResults",
    SingleWinner = "singleWinner",
    FiveMatchPool = "fiveMatchPool",
    ShopsQRCode = "shopsQRCode",
}

interface ProductConfig {
    routeName: string;
    baseUrl: string;
    name: string;
    pool: Pool;
    features: Feature[];
}

const productConfigs: ProductConfig[] = [
    {
        routeName: "big9",
        baseUrl: "/sport/big9",
        name: "Big 9",
        pool: Pool.Big9,
        features: [
            Feature.Wildcard,
            Feature.Harry,
            Feature.GameTips,
            Feature.HowBig9Works,
            Feature.SingleWinner,
            Feature.ShopsQRCode,
        ],
    },
    {
        routeName: "daily5",
        baseUrl: "/sport/daily5",
        name: "Daily 5",
        pool: Pool.Sp5,
        features: [
            Feature.UpcomingOfferings,
            Feature.MultipleLastResults,
            Feature.FiveMatchPool,
        ],
    },
];
interface SportConfig {
    routeName: string;
    displayName: string;
    systemName: string;
    overUnderLimit: number;
}

export const sportConfigs: SportConfig[] = [
    {
        routeName: "fotboll",
        displayName: "Fotboll",
        systemName: PoolSports.Football,
        overUnderLimit: 2.5,
    },
    {
        routeName: "hockey",
        displayName: "Hockey",
        systemName: PoolSports.Hockey,
        overUnderLimit: 5.5,
    },
];

export const getPoolSportByRouteName = (routeName?: string): PoolSports | undefined => {
    const sportEntry = sportConfigs.find((config) => config.routeName === routeName);
    return sportEntry?.systemName as PoolSports | undefined;
};

export const getRouteNameBySport = (sport: PoolSports | string): string => {
    const sportEntry = sportConfigs.find((config) => config.systemName === sport);
    return sportEntry?.routeName ?? "fotboll";
};

// based of the url => /sport/daily5/football/live or /sport/big9/live
export function getGameType(pathname: string = window.location.pathname) {
    const [, , type] = pathname.split("/");

    if (type === "daily5") return Pool.Sp5;
    if (type === "big9") return Pool.Big9;

    if (type === "big9-retail" || type === "pool-retail") return Pool.Big9;

    return undefined;
}

// based of the url => /sport/daily5/football/live or /sport/big9/live
export function getSport(pathname: string = window.location.pathname) {
    const [, , , sport] = pathname.split("/");

    const activeSport = sportConfigs.find((config) => config.routeName === sport);

    // default to Football if no sport is found and type is Big9
    if (!activeSport) {
        const type = getGameType(pathname);

        if (type === Pool.Big9) {
            return sportConfigs.find(
                (config) => config.systemName === PoolSports.Football,
            );
        }
    }

    return activeSport;
}

export function usePool() {
    const {pathname} = useLocation();

    const activeGameType = React.useMemo(
        () =>
            productConfigs.find((config) => {
                const type = getGameType(pathname);
                return config.pool === type;
            }),
        [pathname],
    );

    const activeSport = React.useMemo(() => getSport(pathname), [pathname]);

    const featureSupport = React.useMemo(
        () =>
            Object.values(Feature).reduce(
                (acc, feature) => ({
                    ...acc,
                    [feature]: activeGameType?.features.includes(feature) || false,
                }),
                {} as Record<Feature, boolean>,
            ),
        [activeGameType],
    );

    // Create dynamic baseUrl with sport if available
    const baseUrlWithSport = React.useMemo(() => {
        if (!activeGameType) return undefined;
        return activeSport && activeGameType?.pool === Pool.Sp5
            ? `${activeGameType.baseUrl}/${activeSport.routeName}`
            : activeGameType.baseUrl;
    }, [activeGameType, activeSport]);

    return {
        type: activeGameType?.pool,
        name: activeGameType?.name,
        baseUrl: baseUrlWithSport,
        isSp5: activeGameType?.pool === Pool.Sp5,
        isBig9: activeGameType?.pool === Pool.Big9,
        sport: activeSport?.systemName,
        overUnderLimit: activeSport?.overUnderLimit,
        featureSupport,
    };
}
